// 개시 순간의 결과, **rally 마다 한 번 정하고 얼린다**
//
// ── 무엇에 쓰는가 ───────────────────────────────────────────────────────────
//
// defense 탭 상단 신호 줄은 개시가 지난 뒤 세 경우를 가른다 (2026-10-04 사용자): 「소리는 꺼져
// 있었습니다」, 「소리는 제때 울렸습니다」, 「소리는 울리지 않았습니다」. 화면이 개시 순간을 못
// 봤으면(개시 뒤에 열었거나 개시 뒤에 처음 골랐으면) 꼬리를 붙이지 않는다. 한 줄로 접으면 그
// 줄이 앱이 못 울림, 제때 울림, 사람이 끔 셋 모두에 똑같아진다 (2026-09-04 대리 관측의 감점).
//
// ── 왜 attack 화면이 적는가 ─────────────────────────────────────────────────
//
// 소리가 실제로 울렸는지는 신호 런타임이 attack 화면의 시계로 정한다. 같은 사실을 껍데기의
// 다른 시계로 재면 두 시계가 어긋난 동안 개시 전후의 끄기가 반대로 분류된다 (2026-10-04 적대
// 리뷰). 그래서 런타임과 **같은 시계, 같은 틱**에서 런타임이 낸 상태를 그대로 읽어 적는다.
//
// ── 왜 얼리는가 ─────────────────────────────────────────────────────────────
//
// 시계는 동기를 잃으면 기기 시계로 떨어지고, 기기가 늦으면 개시 앞으로 되돌아간다. 그때 다시
// 「개시 전」으로 적으면 개시 뒤에 끈 것이 개시 순간의 끔이 된다 (적대 리뷰가 재현). 한 번 정한
// 결과는 그 rally 의 개시 시각이 바뀌기 전까지 바꾸지 않는다.
//
// ── 왜 열쇠가 rally 인가 ────────────────────────────────────────────────────
//
// 이름과 시각을 열쇠로 쓰면 개명 한 번에 기억이 사라지고, 고르기를 풀었다 다시 고르면 대상이
// 비는 틱에서 버려진다. 그러면 개시를 지켜본 화면이 「못 봄」으로 떨어진다 (적대 리뷰).
//
// ── 「지켜봤다」는 개시 순간에 그 rally 를 고르고 있었다는 뜻이다 ─────────────
//
// 개시 전에 골랐다가 다른 rally 로 옮기거나 고르기를 풀었으면, 그 rally 의 개시 전 기록은 지운다.
// 그 사이 개시가 지났다면 화면은 그것을 지켜보지 않았고, 사용자 결정은 그때 꼬리를 붙이지 않는다
// (2026-10-04 사용자: 개시 뒤에 열었거나 골랐으면 꼬리 없음).
//
// ── 적는 시각 ───────────────────────────────────────────────────────────────
//
// 부르는 쪽(`view-attack/index.tsx`)은 렌더마다 서버 추정으로 새로 읽은 시각과, 그 시계의 동기가 살아
// 있는지를 함께 넘긴다. 동기가 살아 있지 않은 틱에서는 개시 앞뒤를 가르지 않는다(아래 `OpenBook` 주석).

import type { SignalStatus } from '../audio/runtime.ts'

/** 개시 순간에 일어난 일. `null` 은 화면이 그것을 확인하지 못했다는 뜻이다. */
export type OpenOutcome = 'muted' | 'rang' | 'missed'

/** rally 하나의 기록. */
export interface OpenWatch {
  readonly openMs: number
  /** 개시 전에 마지막으로 본 끔 여부. **개시 전을 한 번도 못 봤으면 `null`.** */
  readonly mutedBeforeOpen: boolean | null
  readonly outcome: OpenOutcome | null
  /** 결과를 정했는가. 정한 뒤에는 결과를 바꾸지 않는다. */
  readonly settled: boolean
  /**
   * 이 rally 의 이 개시를 이어 고른 채 런타임이 「제때 울렸다」고 한 적이 있는가 (T6 개정 셋째, 2026-10-05 세션 설계).
   *
   * 결과를 모르는 rally 에서도 런타임은 울릴 수 있다(개시 전에 시계 동기가 없어 적지 못했을 때). attack 상자는 그때
   * 「제때 울렸습니다」를 띄우는데, 끄면 런타임 상태가 「껐음」으로 바뀌어 그 근거가 사라졌고 상자가 늦음 경고로 바뀌었다
   * (다섯째 판 리뷰). 이 표지가 그 사실을 지킨다. defense 줄은 결과(모름)를 따르므로 꼬리 없음 그대로다.
   */
  readonly rangSeen: boolean
}

/**
 * 기록 묶음. rally ID 를 열쇠로 쓴다.
 *
 * ── 왜 동기가 살아 있는 틱에서만 개시 앞뒤를 가르는가 ─────────────────────
 *
 * 화면 시계는 동기를 잃거나 기기 벽시계가 뛰면 기기 시각으로 떨어진다. 기기가 늦으면 시각이 뒤로
 * 가서 개시 뒤에 처음 고른 rally 가 「개시 전」으로 적혔고(이 머신은 30초마다 약 2.4초, 브라우저 검사가
 * 잡았다), 기기가 앞서면 개시 전에 결과가 「못 울림」으로 얼었다(넷째 판 적대 리뷰가 재현). 그래서 동기가
 * 살아 있지 않은 틱에서는 개시 앞뒤를 가르지 않는다(아래 `distrust`). 처음에는 기록 안의 시각을 최고 수위로 붙들었는데, 그 값이 렌더마다
 * 새 상태를 만들어 화면을 끝없이 다시 그리게 했고(넷째 판 리뷰가 재현) 동기 전의 앞선 기기 시각에도 붙었다.
 */
export interface OpenBook {
  readonly watches: Readonly<Record<string, OpenWatch>>
  /**
   * 직전 틱에 고르고 있던 rally 와 그 개시 시각. 대상이 바뀐 첫 렌더의 런타임 상태는 앞 대상의 것이라(셋째 판 리뷰가
   * 재현한 낡음), 「제때 울렸다」는 같은 rally 의 같은 개시를 이어 고른 틱에서만 믿는다. 개시 시각까지 견주는 것은 CT 가
   * 같은 rally 를 다시 잡은 첫 렌더에서 앞 개시의 「울렸음」이 새 개시에 붙었기 때문이다(T6 개정 셋째 리뷰가 재현).
   */
  readonly lastPick: { readonly rallyId: string; readonly openMs: number } | null
}

export const EMPTY_OPEN_BOOK: OpenBook = { watches: {}, lastPick: null }

/** 한 틱에 고르고 있는 것. 고른 것이 없으면 `null`. */
export interface OpenPick {
  readonly rallyId: string
  readonly openMs: number
  readonly muted: boolean
  readonly state: SignalStatus['state']
}

/**
 * 기록을 한 틱 넘긴다. `nowMs` 는 런타임과 같은 시계의 지금이고, `synced` 는 그 시계가 서버와 동기가
 * 살아 있는가다. **바뀐 것이 없으면 같은 참조를 돌려준다.** 이 값을 상태로 든 화면이 틱마다 다시 그리지
 * 않게 하려는 것이다.
 *
 * 개시 그 순간(`nowMs === openMs`)은 개시 전으로 적는다. 카운트다운과 defense 줄이 그 순간을
 * 「지나가지 않았다」로 보는 것과 같은 경계다.
 */
export function watchOpen(
  book: OpenBook,
  nowMs: number,
  pick: OpenPick | null,
  synced: boolean,
): OpenBook {
  // **고르고 있지 않은 rally 의 「개시 전」 기록은 지운다.** 개시 전에 골랐다가 다른 rally 로 옮긴 사이
  // 개시가 지나면, 그 개시는 화면이 지켜본 것이 아니다. 기록을 남겨 두면 개시 뒤에 다시 고른 첫 틱이
  // 그것으로 결과를 정해, 직전 rally 의 「제때 울림」이나 옮기기 전의 「끔」으로 얼었다 (2026-10-04
  // 셋째 판 적대 리뷰가 둘 다 재현). 정한 결과는 지우지 않는다. 다시 골라도 지켜본 개시를 잃지 않는다.
  let watches = forgetOthers(book.watches, pick?.rallyId ?? null)
  if (pick !== null) {
    const held = watches[pick.rallyId]
    // 동기가 없는 틱은 이미 있는 같은 개시의 기록만 손본다. 새 기록은 시각을 믿을 수 있을 때 시작한다.
    const continued =
      book.lastPick !== null && book.lastPick.rallyId === pick.rallyId && book.lastPick.openMs === pick.openMs
    if (!synced && (held === undefined || held.openMs !== pick.openMs)) {
      // 동기가 없어 개시 앞뒤는 못 가르지만, 같은 개시를 이어 고른 채 런타임이 울렸다는 사실은 시각과 상관없다. 그것만은
      // 남긴다. 안 그러면 동기 없이 제때 울린 뒤 끈 사람이 동기가 돌아온 뒤 늦음 경고를 봤다(T6 개정 셋째 리뷰가 재현).
      if (continued && pick.state === 'passed') {
        watches = {
          ...watches,
          [pick.rallyId]: { openMs: pick.openMs, mutedBeforeOpen: null, outcome: null, settled: false, rangSeen: true },
        }
      }
      return finish(book, watches, pick)
    }
    const watch: OpenWatch =
      held !== undefined && held.openMs === pick.openMs
        ? held
        : { openMs: pick.openMs, mutedBeforeOpen: null, outcome: null, settled: false, rangSeen: false }
    let next = watch.settled ? watch : synced ? advance(watch, pick, nowMs) : distrust(watch, pick)
    if (!next.rangSeen && pick.state === 'passed' && continued) {
      next = { ...next, rangSeen: true }
    }
    if (next !== held) watches = { ...watches, [pick.rallyId]: next }
  }
  return finish(book, watches, pick)
}

function finish(book: OpenBook, watches: OpenBook['watches'], pick: OpenPick | null): OpenBook {
  const last = book.lastPick
  const same =
    pick === null ? last === null : last !== null && last.rallyId === pick.rallyId && last.openMs === pick.openMs
  if (watches === book.watches && same) return book
  return { watches, lastPick: same ? last : pick === null ? null : { rallyId: pick.rallyId, openMs: pick.openMs } }
}

/**
 * 동기가 살아 있지 않은 틱. **시각을 믿을 수 없으니 개시 앞뒤를 가르지 않고 결과도 정하지 않는다.** 다만
 * 그 사이 끔이 바뀌었으면 그 변화가 개시 앞인지 뒤인지 모르므로 개시 전 기록을 모름으로 비운다. 처음에는
 * 이런 틱을 통째로 버렸는데, 그 사이에 켠 것이 빠져 실제로 울린 개시가 「꺼져 있었음」으로 얼었다(다섯째
 * 판 적대 리뷰가 재현). 다른 rally 의 개시 전 기록 지우기는 시각과 상관없으니 위에서 언제나 한다.
 */
function distrust(watch: OpenWatch, pick: OpenPick): OpenWatch {
  if (watch.mutedBeforeOpen === null || watch.mutedBeforeOpen === pick.muted) return watch
  return { ...watch, mutedBeforeOpen: null }
}

function advance(watch: OpenWatch, pick: OpenPick, now: number): OpenWatch {
  if (now <= pick.openMs) {
    return watch.mutedBeforeOpen === pick.muted ? watch : { ...watch, mutedBeforeOpen: pick.muted }
  }
  const decided = decide(watch.mutedBeforeOpen, pick.state, pick.muted)
  if (decided === 'wait') return watch
  return { ...watch, outcome: decided, settled: true }
}

/**
 * 개시를 지난 틱에서 결과를 정한다. `'wait'` 이면 아직 정하지 않는다.
 *
 * 런타임은 개시를 지난 첫 틱에 예약이 정직했으면 `passed` 를 낸다. 화면의 상태 값은 한 틱 늦게
 * 올 수 있으므로 그 사이의 `scheduled` 는 기다린다. 개시 뒤 첫 정착이 사람의 끔이면 출발음이
 * 났는지 확인하지 못했으므로 모른다고 적는다.
 */
function decide(
  mutedBeforeOpen: boolean | null,
  state: SignalStatus['state'],
  muted: boolean,
): OpenOutcome | null | 'wait' {
  if (mutedBeforeOpen === null) return null
  if (mutedBeforeOpen) return 'muted'
  switch (state) {
    case 'scheduled':
      return 'wait'
    // 켠 직후에는 런타임이 다음 틱에야 다시 걸어, 화면의 상태 값이 「껐음」으로 남아 있다. 지금 켜져
    // 있는데 상태가 「껐음」이면 그것은 낡은 값이라 다음 정착을 기다린다 (셋째 판 적대 리뷰).
    case 'muted':
      return muted ? null : 'wait'
    case 'passed':
      return 'rang'
    case 'missed':
    case 'locked':
      return 'missed'
    case 'idle':
      return null
  }
}

/** 고르고 있는 rally 가 아닌 것 가운데 아직 정하지 않은 기록의 「개시 전」을 비운다. */
function forgetOthers(
  watches: OpenBook['watches'],
  keep: string | null,
): OpenBook['watches'] {
  let next: Record<string, OpenWatch> | null = null
  for (const [rallyId, watch] of Object.entries(watches)) {
    if (rallyId === keep || watch.settled || watch.mutedBeforeOpen === null) continue
    next ??= { ...watches }
    next[rallyId] = { ...watch, mutedBeforeOpen: null }
  }
  return next ?? watches
}

/** 그 rally 의 그 개시에서 이어 고른 채 런타임이 제때 울렸다고 한 적이 있는가. */
export function rangSeenOf(book: OpenBook, rallyId: string, openMs: number): boolean {
  const watch = book.watches[rallyId]
  return watch !== undefined && watch.openMs === openMs && watch.rangSeen
}

/** 그 rally 의 그 개시에 대해 결과를 정했는가(모름으로 정한 것도 포함). */
export function outcomeSettled(book: OpenBook, rallyId: string, openMs: number): boolean {
  const watch = book.watches[rallyId]
  return watch !== undefined && watch.openMs === openMs && watch.settled
}

/** 그 rally 의 그 개시에 대해 정한 결과. 아직 안 정했거나 개시 시각이 바뀌었으면 `null`. */
export function outcomeOf(book: OpenBook, rallyId: string, openMs: number): OpenOutcome | null {
  const watch = book.watches[rallyId]
  if (watch === undefined || watch.openMs !== openMs || !watch.settled) return null
  return watch.outcome
}

/** 신호 존이 개시 뒤 상자를 고르는 데 쓰는 세 값. */
export interface OpenView {
  readonly outcome: OpenOutcome | null
  readonly outcomeSettled: boolean
  readonly rangSeen: boolean
}

/**
 * 고른 대상의 세 값을 한 번에 읽는다. attack 화면은 이것을 신호 존에 그대로 펼쳐 넘긴다. 화면이 셋을 따로 읽어 넘기면
 * 하나를 빠뜨려도 타입 검사가 못 잡는다(신호 존의 그 속성이 늘 거짓이 되어 끈 뒤 「제때 울렸습니다」 상자가 사라진다).
 * 그래서 읽는 자리를 하나로 모으고 단위 검사가 이 함수를 붙든다(T6 개정 셋째 셋째 판 리뷰).
 */
export function openViewOf(book: OpenBook, target: { readonly rallyId: string; readonly openMs: number } | null): OpenView {
  if (target === null) return { outcome: null, outcomeSettled: false, rangSeen: false }
  return {
    outcome: outcomeOf(book, target.rallyId, target.openMs),
    outcomeSettled: outcomeSettled(book, target.rallyId, target.openMs),
    rangSeen: rangSeenOf(book, target.rallyId, target.openMs),
  }
}
